import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Popover, Checkbox, Button } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  SettingOutlined,
  LeftOutlined,
  RightOutlined,
  CaretUpOutlined,
  CaretDownOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import Spinner from '../../CustomComponents/Spinner';
import {
  C,
  ListColumn,
  ListHead,
  ListSearchBar,
  ListSearch,
  ListFilterButton,
  ListRows,
  ListHeaderRow,
  HeaderCell,
  HeaderLabel,
  HeaderSortIcon,
  HeaderFilterIcon,
  ResizeHandle,
  FilterMenu,
  ListRow,
  Cell,
  EmptyState,
  ListFoot,
  PageInfo,
  PageControls,
  PageBtn
} from './AuditDetailStyles';

const PAGE_SIZE = 50;

// Keep action columns (e.g. Strike) pinned last; drop unknown/missing keys.
const normalizeOrder = (columns, keys) => {
  const actionKeys = columns.filter(c => c.action).map(c => c.key);
  const nonAction = columns.filter(c => !c.action).map(c => c.key);
  const kept = (keys || []).filter(k => nonAction.includes(k));
  nonAction.filter(k => !kept.includes(k)).forEach(k => kept.push(k));
  return [...kept, ...actionKeys];
};

const loadPersisted = storageKey => {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey)) || {};
  } catch (e) {
    return {};
  }
};

const Sorter = ({ dir }) => (
  <>
    <CaretUpOutlined
      style={{ fontSize: 9, color: dir === 'asc' ? C.brandBlue : C.placeholder }}
    />
    <CaretDownOutlined
      style={{
        fontSize: 9,
        marginTop: -3,
        color: dir === 'desc' ? C.brandBlue : C.placeholder
      }}
    />
  </>
);

/**
 * Reusable audit list — a horizontally scrollable table with per-column sort,
 * multi-select filters, resize, drag-reorder, cross-column search, Excel export
 * and (optional) column customization. Filter/sort/order/width state persists
 * to sessionStorage under `storageKey`. Used for the Jobs, Users and Facilities
 * tabs of the audit split view. Give it a stable `key` per tab so state resets.
 */
const AuditList = ({
  columns,
  rows: data = [],
  getRowId,
  selectedId,
  onSelect,
  cellCtx,
  visibleColumns,
  onOpenColumnSettings,
  onExportReady,
  clearFiltersTrigger,
  loading = false,
  storageKey = 'audit_list_state',
  searchPlaceholder = 'Search…',
  emptyText = 'No records match your search.',
  defaultSort = null,
  defaultFilters = null,
  exportName = 'audit'
}) => {
  const columnMap = useRef(
    Object.fromEntries(columns.map(c => [c.key, c]))
  ).current;
  const defaultWidths = useRef(
    Object.fromEntries(columns.map(c => [c.key, c.size]))
  ).current;
  const persisted = useRef(loadPersisted(storageKey)).current;

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(persisted.sort || null);
  const [filters, setFilters] = useState(
    persisted.filters || defaultFilters || {}
  );
  const [order, setOrder] = useState(normalizeOrder(columns, persisted.order));
  const [widths, setWidths] = useState({
    ...defaultWidths,
    ...(persisted.widths || {})
  });
  const [openFilter, setOpenFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [draggedKey, setDraggedKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const colFor = key => columnMap[key];

  // Persist filter / sort / order / width state.
  useEffect(() => {
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({ sort, filters, order, widths })
    );
  }, [sort, filters, order, widths, storageKey]);

  // Clear-all filters + search (triggered from the toolbar).
  useEffect(() => {
    if (clearFiltersTrigger) {
      setFilters({});
      setSearch('');
      setPage(0);
    }
  }, [clearFiltersTrigger]);

  const toggleSort = key => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
    setPage(0);
  };

  const setColumnFilter = (key, values) => {
    setFilters(prev => ({ ...prev, [key]: values }));
    setPage(0);
  };

  const distinctFor = key => {
    const col = colFor(key);
    return Array.from(new Set(data.map(r => col.text(r)))).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  };

  // ----- column reorder (HTML5 drag on the label) -----
  const onDrop = targetKey => {
    const target = colFor(targetKey);
    if (!draggedKey || draggedKey === targetKey || target?.action) {
      setDraggedKey(null);
      setDragOverKey(null);
      return;
    }
    setOrder(prev => {
      const actionKeys = columns.filter(c => c.action).map(c => c.key);
      const arr = prev.filter(k => !actionKeys.includes(k));
      const from = arr.indexOf(draggedKey);
      const to = arr.indexOf(targetKey);
      if (from < 0 || to < 0) return prev;
      arr.splice(from, 1);
      arr.splice(to, 0, draggedKey);
      return [...arr, ...actionKeys];
    });
    setDraggedKey(null);
    setDragOverKey(null);
  };

  // ----- column resize (mouse drag on the grip) -----
  const startResize = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key] || colFor(key).size;
    const onMove = ev => {
      const w = Math.max(60, startW + (ev.clientX - startX));
      setWidths(prev => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ----- data pipeline: search (all columns) → filters → sort -----
  const term = search.trim().toLowerCase();
  const activeFilters = Object.entries(filters).filter(([, v]) => v && v.length);
  let rows = data.filter(row => {
    if (
      term &&
      !columns.some(
        c => !c.action && String(c.text(row)).toLowerCase().includes(term)
      )
    ) {
      return false;
    }
    return activeFilters.every(([key, vals]) =>
      vals.includes(colFor(key).text(row))
    );
  });

  const effectiveSort = sort || defaultSort;
  if (effectiveSort && colFor(effectiveSort.key)) {
    const col = colFor(effectiveSort.key);
    const val = row => (col.sortVal ? col.sortVal(row) : col.text(row));
    rows = [...rows].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      let r;
      if (av == null && bv == null) r = 0;
      else if (av == null) r = 1;
      else if (bv == null) r = -1;
      else if (typeof av === 'number' && typeof bv === 'number') r = av - bv;
      else r = String(av).localeCompare(String(bv));
      return effectiveSort.dir === 'desc' ? -r : r;
    });
  }

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);

  const start = page * PAGE_SIZE;
  const visibleRows = rows.slice(start, start + PAGE_SIZE);

  // Ordered + visible columns and the shared grid template.
  const orderedCols = order.map(k => columnMap[k]).filter(Boolean);
  const cols = orderedCols.filter(
    c => c.action || !visibleColumns || visibleColumns[c.key] !== false
  );
  const template = cols.map(c => `${widths[c.key] || c.size}px`).join(' ');

  // ----- Excel export (stable fn; reads latest data via ref) -----
  const exportRef = useRef();
  exportRef.current = { cols, rows };
  const exportToExcel = useCallback(() => {
    const { cols: exCols, rows: exRows } = exportRef.current;
    const exportCols = exCols.filter(c => !c.action);
    const rowsData = exRows.map(row => {
      const rowData = {};
      exportCols.forEach(c => {
        rowData[c.label] = c.text(row);
      });
      return rowData;
    });
    const worksheet = XLSX.utils.json_to_sheet(rowsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit');
    XLSX.writeFile(
      workbook,
      `${exportName}_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  }, [exportName]);
  useEffect(() => {
    if (onExportReady) onExportReady(exportToExcel);
  }, [exportToExcel, onExportReady]);

  return (
    <ListColumn>
      <ListHead>
        <ListSearchBar>
          <ListSearch>
            <SearchOutlined />
            <input
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={searchPlaceholder}
            />
          </ListSearch>
          {onOpenColumnSettings && (
            <ListFilterButton
              type='button'
              aria-label='Customize columns'
              title='Customize columns'
              onClick={onOpenColumnSettings}
            >
              <SettingOutlined />
            </ListFilterButton>
          )}
        </ListSearchBar>
      </ListHead>
      <ListRows>
        <ListHeaderRow style={{ gridTemplateColumns: template }}>
          {cols.map(c => {
            if (c.action) {
              return (
                <HeaderCell key={c.key}>
                  <HeaderLabel as='span' style={{ cursor: 'default' }}>
                    {c.label}
                  </HeaderLabel>
                </HeaderCell>
              );
            }
            const selected = filters[c.key] || [];
            return (
              <HeaderCell
                key={c.key}
                dragOver={dragOverKey === c.key}
                dragging={draggedKey === c.key}
                onDragOver={e => {
                  if (draggedKey && draggedKey !== c.key) {
                    e.preventDefault();
                    setDragOverKey(c.key);
                  }
                }}
                onDragLeave={() => setDragOverKey(null)}
                onDrop={() => onDrop(c.key)}
              >
                <HeaderSortIcon
                  onClick={() => toggleSort(c.key)}
                  title={`Sort by ${c.label}`}
                >
                  <Sorter dir={sort?.key === c.key ? sort.dir : null} />
                </HeaderSortIcon>
                <HeaderLabel
                  draggable
                  onDragStart={() => setDraggedKey(c.key)}
                  onDragEnd={() => {
                    setDraggedKey(null);
                    setDragOverKey(null);
                  }}
                  onClick={() => toggleSort(c.key)}
                  title={`Sort by ${c.label} · drag to reorder`}
                >
                  {c.label}
                </HeaderLabel>
                <Popover
                  trigger='click'
                  placement='bottomRight'
                  visible={openFilter === c.key}
                  onVisibleChange={v => setOpenFilter(v ? c.key : null)}
                  content={
                    <FilterMenu>
                      <div className='list'>
                        <Checkbox.Group
                          value={selected}
                          onChange={vals => setColumnFilter(c.key, vals)}
                        >
                          {distinctFor(c.key).map(v => (
                            <div className='row' key={v}>
                              <Checkbox value={v}>{v}</Checkbox>
                            </div>
                          ))}
                        </Checkbox.Group>
                      </div>
                      <div className='actions'>
                        <Button
                          type='link'
                          size='small'
                          disabled={!selected.length}
                          onClick={() => setColumnFilter(c.key, [])}
                        >
                          Clear
                        </Button>
                        <Button
                          type='primary'
                          size='small'
                          onClick={() => setOpenFilter(null)}
                        >
                          OK
                        </Button>
                      </div>
                    </FilterMenu>
                  }
                >
                  <HeaderFilterIcon
                    active={selected.length > 0}
                    onClick={e => e.stopPropagation()}
                  >
                    <FilterOutlined />
                  </HeaderFilterIcon>
                </Popover>
                <ResizeHandle
                  onMouseDown={e => startResize(c.key, e)}
                  onClick={e => e.stopPropagation()}
                />
              </HeaderCell>
            );
          })}
        </ListHeaderRow>
        {loading ? (
          <Spinner height={'240px'} />
        ) : visibleRows.length ? (
          visibleRows.map(row => {
            const rowId = getRowId(row);
            return (
              <ListRow
                key={rowId}
                selected={rowId === selectedId}
                style={{ gridTemplateColumns: template }}
                onClick={() => onSelect(row)}
              >
                {cols.map(c => (
                  <React.Fragment key={c.key}>
                    {c.cell ? c.cell(row, cellCtx) : <Cell>{c.text(row)}</Cell>}
                  </React.Fragment>
                ))}
              </ListRow>
            );
          })
        ) : (
          <EmptyState>{emptyText}</EmptyState>
        )}
      </ListRows>
      {!loading && rows.length > 0 && (
        <ListFoot>
          <PageInfo>
            {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of{' '}
            {rows.length}
          </PageInfo>
          <PageControls>
            <PageBtn
              type='button'
              aria-label='Previous page'
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              <LeftOutlined />
            </PageBtn>
            <PageInfo>
              {page + 1} / {pageCount}
            </PageInfo>
            <PageBtn
              type='button'
              aria-label='Next page'
              disabled={page >= pageCount - 1}
              onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
            >
              <RightOutlined />
            </PageBtn>
          </PageControls>
        </ListFoot>
      )}
    </ListColumn>
  );
};

export default AuditList;
